/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import { HEADER_AUTHORIZATION } from '@ecsper/api-client';
import type { UUIDv7 } from '@ecsper/types';

/**
 * VaultClient - wraps Workspace-mediated DOCX operations.
 *
 * The add-in saves/fetches DOCX documents through Workspace,
 * which internally delegates to VaultAdapter. This client
 * provides typed access to those endpoints.
 *
 * @see CTR-MS-VL-DOCSAVE-001 v1.0.0
 * @see CTR-MS-WK-REPORT-001 v1.0.0
 */

export interface VaultClientConfig {
  /** Base URL for Workspace API (e.g., https://dev.api.ecsper.io/api/v1/workspace). */
  baseUrl: string;
  /** Function that returns a valid bearer token. */
  getToken: () => Promise<string>;
}

export interface SaveDocxParams {
  reportId: UUIDv7;
  docxBlob: Blob;
  saveType: 'manual' | 'auto' | 'final';
  clientVersion?: string;
  /** When true, marks the version as a major milestone (Stage 6 coalescing exclusion). */
  isMajor?: boolean;
  /** Optional user note for major versions. */
  versionNote?: string;
}

export interface SaveDocxResult {
  versionId: string;
  versionNumber: number;
  contentHash: string;
  createdAt: string;
  isDuplicate: boolean;
  isMajor: boolean;
}

export interface FetchDocxResult {
  blob: Blob;
  contentType: string;
  versionId: string;
}

export interface ReportVersionSummary {
  id: string;
  versionNumber: number;
  contentHash: string;
  saveSource: string;
  createdAt: string;
  createdByActorId: string;
  isMajor: boolean;
}

export class VaultClient {
  private readonly config: VaultClientConfig;

  public constructor(config: VaultClientConfig) {
    this.config = config;
  }

  /**
   * Save a DOCX file for a report. Creates a new version row
   * and stores the binary in Vault via Workspace's VaultAdapter.
   */
  public async saveDocx(params: SaveDocxParams): Promise<SaveDocxResult> {
    const token = await this.config.getToken();
    const formData = new FormData();
    formData.append('file', params.docxBlob, 'report.docx');
    formData.append('save_type', params.saveType);
    if (params.clientVersion !== undefined) {
      formData.append('client_version', params.clientVersion);
    }
    if (params.isMajor === true) {
      formData.append('is_major', 'true');
    }
    if (params.versionNote !== undefined && params.versionNote !== '') {
      formData.append('version_note', params.versionNote);
    }

    const response = await fetch(
      `${this.config.baseUrl}/expert/reports/${params.reportId}/save-docx/`,
      {
        method: 'POST',
        headers: {
          [HEADER_AUTHORIZATION]: `Bearer ${token}`,
          'X-Ecsper-Request-Id': crypto.randomUUID(),
        },
        body: formData,
      },
    );

    if (!response.ok) {
      throw new VaultClientError(`Save failed: ${response.status}`, response.status);
    }

    const data: unknown = await response.json();
    const envelope = data as Record<string, unknown>;
    const inner =
      envelope['data'] !== undefined &&
      typeof envelope['data'] === 'object' &&
      envelope['data'] !== null
        ? (envelope['data'] as Record<string, unknown>)
        : envelope;

    return {
      versionId: String(inner['id'] ?? ''),
      versionNumber: Number(inner['version_number'] ?? 0),
      contentHash: String(inner['content_hash_sha256'] ?? ''),
      createdAt: String(inner['created_at'] ?? ''),
      isDuplicate: Boolean(inner['is_duplicate'] ?? false),
      isMajor: Boolean(inner['is_major'] ?? false),
    };
  }

  /**
   * Fetch the DOCX binary for a specific report version.
   */
  public async fetchDocx(reportId: UUIDv7, versionId: string): Promise<FetchDocxResult> {
    const token = await this.config.getToken();
    const response = await fetch(
      `${this.config.baseUrl}/expert/reports/${reportId}/versions/${versionId}/docx/`,
      {
        method: 'GET',
        headers: {
          [HEADER_AUTHORIZATION]: `Bearer ${token}`,
          'X-Ecsper-Request-Id': crypto.randomUUID(),
        },
      },
    );

    if (!response.ok) {
      throw new VaultClientError(`Fetch failed: ${response.status}`, response.status);
    }

    const blob = await response.blob();
    return {
      blob,
      contentType:
        response.headers.get('Content-Type') ??
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      versionId,
    };
  }

  /**
   * List all versions for a report.
   */
  public async listVersions(reportId: UUIDv7): Promise<ReportVersionSummary[]> {
    const token = await this.config.getToken();
    const response = await fetch(`${this.config.baseUrl}/expert/reports/${reportId}/versions/`, {
      method: 'GET',
      headers: {
        [HEADER_AUTHORIZATION]: `Bearer ${token}`,
        'X-Ecsper-Request-Id': crypto.randomUUID(),
      },
    });

    if (!response.ok) {
      throw new VaultClientError(`List versions failed: ${response.status}`, response.status);
    }

    const data: unknown = await response.json();
    const envelope = data as Record<string, unknown>;
    const dataField = envelope['data'];
    const nestedResults =
      dataField !== undefined &&
      typeof dataField === 'object' &&
      dataField !== null &&
      !Array.isArray(dataField) &&
      Array.isArray((dataField as Record<string, unknown>)['results'])
        ? ((dataField as Record<string, unknown>)['results'] as unknown[])
        : undefined;
    const items: unknown[] =
      nestedResults ??
      (Array.isArray(envelope['results']) ? envelope['results'] : undefined) ??
      (Array.isArray(dataField) ? dataField : undefined) ??
      [];

    return (items as Record<string, unknown>[]).map((v: Record<string, unknown>) => ({
      id: v['id'] as string,
      versionNumber: v['version_number'] as number,
      contentHash: v['content_hash_sha256'] as string,
      saveSource: v['save_source'] as string,
      createdAt: v['created_at'] as string,
      createdByActorId: v['created_by_actor_id'] as string,
      isMajor: Boolean(v['is_major'] ?? false),
    }));
  }
}

export class VaultClientError extends Error {
  public override readonly name = 'VaultClientError';
  public readonly statusCode: number;

  public constructor(message: string, statusCode: number) {
    super(message);
    this.statusCode = statusCode;
  }
}
